import { useCallback, useEffect, useState } from 'react'
import { Bot } from 'lucide-react'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { reportError } from 'utils/errorReporter'
import { fetchBoxTruckOfferBot, fetchConversationBot, setBoxTruckOfferBot } from '../services/boxTruckLoadsApi'
import { useConversationBot } from '../hooks/useConversationBot'
import { trackBoxTruckOfferBotToggled } from '../utils/analytics'

/**
 * The load bot, directly above the box a dispatcher types into (ADR-057 §14).
 *
 * Here rather than in the page header because the question is about the conversation on screen, not the
 * page: in the corner it said the company's state while a single driver sat paused underneath it.
 *
 * Both scopes in one bar, sized by how often each is used, and the line says WHOSE decision the silence
 * was — three different people can produce it and a dispatcher can undo only their own.
 */
export const SmsBotBar = ({ driverAccountId }: { driverAccountId: string }) => {
    const { showSnackbar } = useSnackbar()
    // This conversation's half — state, the stale-answer guard, the words — is the shared hook's, so the
    // same switch in the load drawer cannot say something different about the same driver.
    const conversation = useConversationBot(driverAccountId, 'conversation_bar')
    const { bot, off, mine, label, hint, saving: switching, toggle: toggleConversation, adopt } = conversation
    /** How many drivers took themselves out with STOP — the number to read before deciding the bot is
     *  broken. Not a list: who they are is their own business. */
    const [optedOut, setOptedOut] = useState(0)
    /** Whether this operator may flip the company's switch — the server's answer, not a role read here.
     *  Starts false, so a read that has not landed offers nothing a press could only 403 on. */
    const [canChangeCompany, setCanChangeCompany] = useState(false)
    /** Whether the rollout has reached this company at all — fail-closed, like the bot's own gate. */
    const [reached, setReached] = useState(true)
    const [savingTenant, setSavingTenant] = useState(false)
    const saving = switching || savingTenant

    useEffect(() => {
        let cancelled = false
        void fetchBoxTruckOfferBot()
            .then((answer) => {
                if (cancelled) return
                setOptedOut(answer.optedOutCount)
                setCanChangeCompany(answer.canChange)
                setReached(answer.reached)
            })
            .catch((error) => {
                // Reported, not only logged: the bar draws nothing when these fail and the operator is told
                // nothing either, so a console line is a failure nobody ever hears about.
                reportError({
                    code: 'box_truck_offer_bot_read',
                    errorMessage: getUserErrorMessage(error) || 'offer-bot read failed',
                    feature: 'box-truck-loads',
                })
            })
        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        // Re-read per driver, dropping the old answer first: the bar stays mounted while the selected
        // conversation changes under it, and a switch showing the previous driver's state is the one thing
        // it must never do.
        let cancelled = false
        adopt(null)
        void fetchConversationBot(driverAccountId)
            .then((answer) => {
                if (!cancelled) adopt(answer)
            })
            // Silent, and the bar simply does not draw: an operator reading their messages did not ask
            // about a switch, and a checkbox whose position is a guess is worse than no checkbox.
            .catch((error) =>
                reportError({
                    code: 'box_truck_conversation_bot_read',
                    errorMessage: getUserErrorMessage(error) || 'conversation bot read failed',
                    feature: 'box-truck-loads',
                })
            )
        return () => {
            cancelled = true
        }
    }, [driverAccountId, adopt])

    /** The company. Flipping it changes every conversation at once, so this one says so out loud. */
    const toggleTenant = useCallback(
        async (on: boolean) => {
            setSavingTenant(true)
            try {
                // THE SAVE, AND NOTHING ELSE IN THIS TRY: with the re-read in here too, a refresh that
                // failed counted one action twice and told the operator a saved switch had not saved.
                await setBoxTruckOfferBot(on)
            } catch (error) {
                // Counted because this one switch silences the bot for every driver the company has, and a
                // save that fails leaves only a snackbar behind: without the outcome, "the bot stopped
                // answering" cannot be told apart from "somebody turned it off".
                trackBoxTruckOfferBotToggled({ scope: 'company', surface: 'conversation_bar', on, outcome: 'failure' })
                showSnackbar(SnackbarType.WARNING, getUserErrorMessage(error) || 'Could not change the load bot')
                setSavingTenant(false)
                return
            }
            trackBoxTruckOfferBotToggled({ scope: 'company', surface: 'conversation_bar', on, outcome: 'success' })
            showSnackbar(
                SnackbarType.SUCCESS,
                on ? 'The load bot will answer drivers who text their ZIP' : 'The load bot will not answer any driver until you turn it back on'
            )
            // What it did to THIS conversation, guarded by the driver it was asked about. Silent on
            // failure: the switch is saved and said so, and the next selection corrects the checkbox.
            await fetchConversationBot(driverAccountId)
                .then((answer) => adopt(answer, driverAccountId))
                .catch((error) =>
                    reportError({
                        code: 'box_truck_conversation_bot_reread',
                        errorMessage: getUserErrorMessage(error) || 'conversation bot re-read failed',
                        feature: 'box-truck-loads',
                    })
                )
            setSavingTenant(false)
        },
        [driverAccountId, showSnackbar, adopt]
    )

    if (!bot) return null
    const tenantOff = off === 'tenant'
    /**
     * The company half is read off the same answer, so it has to know about the rollout too: `off === 'tenant'`
     * is false for a company the feature has not reached, and the line would have read "Company: on" over a
     * bot that answers nothing. Not an admin's to turn on either — their switch cannot reach past the flag —
     * so no press is offered, the same way one is never offered to a non-admin.
     */
    // Either source will do, and they answer the same question: the conversation read says `rollout` and the
    // company read says `reached`. Taking both means the line is right as soon as EITHER has landed.
    const unreached = off === 'rollout' || !reached

    return (
        <div className="flex shrink-0 items-center gap-2 border-t border-border bg-surface px-3 py-2 text-xs">
            <label className={`inline-flex items-center gap-1.5 ${mine ? 'text-foreground' : 'text-foreground-muted'}`}>
                <input
                    type="checkbox"
                    checked={bot.on}
                    disabled={saving || !mine}
                    onChange={(event) => void toggleConversation(event.target.checked)}
                    className="h-3.5 w-3.5 rounded border-border accent-accent"
                />
                <Bot size={14} />
                <span className="font-medium">Load bot</span>
            </label>
            {/* The words are the hook's, so this bar and the drawer's pane say the same thing. */}
            <span className="text-foreground-muted" title={label}>
                {hint}
            </span>
            {/* The company's state, at the far end and in words: it is read by everyone and changed by an
                admin (ADR-057 §14), so a dispatcher is TOLD what it is without being offered a press that
                can only 403. The opted-out count rides with it either way — it is the number to read
                before deciding the bot is broken. */}
            {unreached ? (
                <span className="ml-auto shrink-0 text-foreground-muted" title="The load bot has not been switched on for your company yet">
                    Company: not available
                    {/* The opted-out count still rides along: those drivers asked not to be texted, which is
                        true whatever the rollout has reached, and it is the number to read before deciding
                        the bot is broken. */}
                    {optedOut > 0 && <span className="ml-1">· {optedOut} opted out</span>}
                </span>
            ) : canChangeCompany ? (
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => void toggleTenant(tenantOff)}
                    className="ml-auto shrink-0 text-foreground-muted underline-offset-2 hover:text-foreground hover:underline disabled:opacity-60"
                >
                    {tenantOff ? 'Company: off · turn on' : 'Company: on · turn off'}
                    {optedOut > 0 && <span className="ml-1 no-underline">· {optedOut} opted out</span>}
                </button>
            ) : (
                <span className="ml-auto shrink-0 text-foreground-muted" title="Only an admin can switch the load bot for the whole company">
                    {tenantOff ? 'Company: off' : 'Company: on'}
                    {optedOut > 0 && <span className="ml-1">· {optedOut} opted out</span>}
                </span>
            )}
        </div>
    )
}
